import { z } from 'zod';

// One owner account (decision A9). Sign-in is by passkey; the first passkey
// comes from a one-time link made on the server (`sudo mtg invite`).

export const DisplayName = z.string().trim().min(1, 'Enter a name').max(50, 'Keep it under 50 characters');

export const User = z.object({ id: z.string(), displayName: z.string() });
export type User = z.infer<typeof User>;

export const SessionResponse = z.object({ user: User.nullable() });
export type SessionResponse = z.infer<typeof SessionResponse>;

export const UserResponse = z.object({ user: User });

export const InvitePurpose = z.enum(['signup', 'add_passkey']);
export type InvitePurpose = z.infer<typeof InvitePurpose>;

/** What an invite link shows before it's used. */
export const InviteInfo = z.object({
  purpose: InvitePurpose,
  /** signup: the note left on the server. add_passkey: the account's name. */
  name: z.string().nullable(),
  expiresAt: z.string(),
});
export type InviteInfo = z.infer<typeof InviteInfo>;

export const InviteTokenRequest = z.object({ token: z.string().min(1).max(100) });

// The WebAuthn payloads are validated by SimpleWebAuthn on the server, so they
// pass through here as opaque JSON.
const WebAuthnJson = z.record(z.string(), z.unknown());

export const RegisterOptionsRequest = z.object({
  /** Invite token. Omit to add a passkey to the signed-in account. */
  token: z.string().min(1).max(100).optional(),
  /** Required for signup invites. */
  displayName: DisplayName.optional(),
});
export type RegisterOptionsRequest = z.infer<typeof RegisterOptionsRequest>;

export const CeremonyOptions = z.object({ ceremonyId: z.string(), options: WebAuthnJson });

export const CeremonyVerifyRequest = z.object({
  ceremonyId: z.string().min(1).max(100),
  response: WebAuthnJson,
});

/** A passkey prompt that failed in the browser, reported so it shows up in the server log. */
export const PasskeyFailureReport = z.object({
  context: z.enum(['register', 'login']),
  name: z.string().max(100),
  message: z.string().max(500),
  code: z.string().max(100).optional(),
  cause: z.string().max(300).optional(),
});
export type PasskeyFailureReport = z.infer<typeof PasskeyFailureReport>;

// --- Write tokens (decision A5) ---
// For Claude's scripts: collection and deck reads and changes only, never
// sign-in or token management. The secret is shown once, when it's created.

export const TokenName = z.string().trim().min(1, 'Name the token').max(50, 'Keep it under 50 characters');

export const ApiToken = z.object({
  id: z.string(),
  name: z.string(),
  /** First characters of the secret, to tell tokens apart. */
  prefix: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type ApiToken = z.infer<typeof ApiToken>;

export const ApiTokenList = z.object({ tokens: z.array(ApiToken) });
export const CreateTokenRequest = z.object({ name: TokenName });
export const CreatedToken = z.object({ token: ApiToken, secret: z.string() });
export type CreatedToken = z.infer<typeof CreatedToken>;
